import { ValidationRuleTriggers } from "./triggers";
import { InputValidationResultKind, InputValidationResult } from "../input";
import { localize } from "client_legacy/ndaw/culture";
import { IValidationContext } from ".";
import { isNil } from "client/Misc/utils";

class IsNotNullNorUndefinedRule<TValue> extends ValidationRuleTriggers<TValue> {
	private readonly _alternativeMessage: string | undefined;

	constructor(alternativeMessage?: string) {
		super();
		this._alternativeMessage = alternativeMessage;
	}

	public validate(value: TValue): InputValidationResult {
		if (isNil(value)) {
			return {
				kind: InputValidationResultKind.Invalid,
				errorMessage: this._alternativeMessage || localize("_IsRequired")
			};
		}

		return {
			kind: InputValidationResultKind.Valid
		};
	}
}

class IsNullOrUndefined<TValue> extends ValidationRuleTriggers<TValue> {
	private readonly _alternativeMessage: string | undefined;

	constructor(alternativeMessage?: string) {
		super();
		this._alternativeMessage = alternativeMessage;
	}

	public validate(value: TValue): InputValidationResult {
		if (isNil(value)) {
			return {
				kind: InputValidationResultKind.Valid
			};
		}

		return {
			kind: InputValidationResultKind.Invalid,
			errorMessage: this._alternativeMessage || localize("_IsRequired")
		};
	}
}

class IsEqualToOtherRule<TValue, TParent> extends ValidationRuleTriggers<TValue> {
	private readonly _otherField: StringKeyOf<TParent>;
	private readonly _fieldName: string;
	private readonly _alternativeMessage: string | undefined;

	constructor(otherField: StringKeyOf<TParent>, fieldName?: string, alternativeMessage?: string) {
		super();
		this._otherField = otherField;
		this._fieldName = fieldName ?? otherField;
		this._alternativeMessage = alternativeMessage;

		this.validateOn("anyInputFinished");
		this.fixErrorOn("anyInput");
	}

	public validate(value: TValue, validationContext: IValidationContext): InputValidationResult {
		const parent: TParent = validationContext.parentValue as TParent;
		const otherValue: TParent[StringKeyOf<TParent>] | undefined = parent?.[this._otherField];

		if (!value && !otherValue || this.areEqual(value, otherValue)) {
			return {
				kind: InputValidationResultKind.Valid
			};
		}

		return {
			kind: InputValidationResultKind.Invalid,
			errorMessage: (this._alternativeMessage || localize("_MustBeEqualTo"))
				.replace("{0}", this._fieldName)
		};
	}

	private areEqual(a: unknown, b: unknown) {
		return a === b;
	}
}

class IsEqualToRule<TValue> extends ValidationRuleTriggers<TValue> {
	private readonly _value: TValue;
	private readonly _alternativeMessage: string | undefined;

	constructor(value: TValue, alternativeMessage?: string) {
		super();
		this._value = value;
		this._alternativeMessage = alternativeMessage;
	}

	public validate(value: TValue): InputValidationResult {
		const otherValue: TValue = this._value;

		if (!value && !otherValue || value === otherValue) {
			return {
				kind: InputValidationResultKind.Valid
			};
		}

		return {
			kind: InputValidationResultKind.Invalid,
			errorMessage: (this._alternativeMessage || localize("_MustBeEqualTo"))
				.replace("{0}", (otherValue as any)?.toString() ?? "NULL")
		};
	}
}

export {
	IsNotNullNorUndefinedRule,
	IsNullOrUndefined,
	IsEqualToOtherRule,
	IsEqualToRule
};